/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useRef, useState } from 'react';
import { FormGroup, Modal } from 'react-bootstrap';
import Select from 'react-select';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import _CommonMultiSelect from '../../../Components/Utility/FilterAction/CommonMultiSelect';
import _CustomTooltip from '../../../Components/Tooltip/CustomTooltip';
import { SystemMasterSelect } from '../../../Components/helper';
import { PARTNER_OVERVIEW_TIER_ONE, PARTNER_OVERVIEW_TIER_OPTIONS, hasOptions } from './constants';
import type { PartnerOverviewAttribute, PartnerScopeType, PartnerTierOption } from './partner-overview-attribute.types';

interface CommonMultiSelectProps {
  label: React.ReactNode;
  required?: boolean;
  onChange: (selected: Array<PartnerScopeType | string> | null) => void;
  selectedValue: unknown;
  optionList: unknown;
  selectLabel: string;
  selectValue: string;
  className?: string;
  disable?: boolean;
}
const CommonMultiSelect = _CommonMultiSelect as unknown as React.FC<CommonMultiSelectProps>;
const CustomTooltip = _CustomTooltip as unknown as React.FC<{ tooltipText: React.ReactNode; children?: React.ReactNode }>;

interface AttributeCardProps {
  item: PartnerOverviewAttribute;
  index: number;
  supplierTypesByTier: Record<string, PartnerScopeType[]>;
  addEditAccess: boolean;
  deleteAccess: boolean;
  onChange: (value: unknown, index: number, path: string) => void;
  onDelete: (item: PartnerOverviewAttribute, index: number) => void;
  onToggleActive: (item: PartnerOverviewAttribute) => void;
  onAddOption: (index: number) => void;
  onChangeOption: (value: string, index: number, optionIndex: number, field: 'keyName') => void;
  onDeleteOption: (index: number, optionIndex: number) => void;
  duplicateIndexes: number[];
}

/**
 * @description One Partner Overview custom attribute row. A single card serves all four types;
 * only the option editor and the text configuration differ, so the four near-identical card
 * files the product master carries are not repeated here.
 */
function AttributeCard({
  item,
  index,
  supplierTypesByTier,
  addEditAccess,
  deleteAccess,
  onChange,
  onDelete,
  onToggleActive,
  onAddOption,
  onChangeOption,
  onDeleteOption,
  duplicateIndexes,
}: AttributeCardProps) {
  const { t } = useTranslation('modules/partner-overview');
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [showCharLimit, setShowCharLimit] = useState(false);

  const selectedTier = (item?.tierType ?? [])[0] ?? null;
  const selectedTierOption = PARTNER_OVERVIEW_TIER_OPTIONS.find((option) => option._id === selectedTier) ?? null;

  const isTierOne = selectedTier === PARTNER_OVERVIEW_TIER_ONE;
  const tierTypes: PartnerScopeType[] = selectedTier ? supplierTypesByTier?.[selectedTier] ?? [] : [];
  const typeOptions = tierTypes.filter((type) => type?.isActive !== false);
  const typeLabel = isTierOne ? t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.PRODUCT_TYPE') : t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.COMPONENT_TYPE');

  const selectedTypeIds = item?.supplierType ?? [];

  /**
   * True while any option is flagged blank by the last validation and still blank. The options
   * panel is collapsed by default, so without this the "complete the highlighted fields" toast
   * would point at a message the user cannot see.
   */
  const hasBlankOption = (item?.options ?? []).some((option) => option?.isEmpty && isEmpty((option?.keyName ?? '').trim()));

  /** Same reasoning as hasBlankOption: a message inside a collapsed panel is no message at all. */
  const hasDuplicateOption = (item?.options ?? []).some((option) => option?.isDuplicate);

  const showOptions = optionsOpen || hasBlankOption || hasDuplicateOption;

  /**
   * Per decision D19 an attribute that holds values can still be deactivated and deleted:
   * deactivating only hides the field, and deleting frees the slot while leaving saved values
   * in place. Deleting one in use is confirmed in the popup rather than blocked here.
   */
  const activeStateLabel = item?.isActive ? 'Active' : 'Inactive';
  const activeStateTooltip = activeStateLabel;

  /**
   * Changing tier clears the chosen types: Tier 1 Product Types and Tier 2-4 Component Types are
   * different records, so ids carried over from the previous tier would never match.
   */
  const handleTierChange = (option: PartnerTierOption | null) => {
    onChange(option?._id ? [option._id] : [], index, 'tierType');
    onChange([], index, 'supplierType');
  };

  const toSelected = (ids: string[], options: PartnerScopeType[]) => options.filter((option) => ids.map(String).includes(String(option._id)));

  const typeSelectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const typeSelect = typeSelectRef.current;
    if (!typeSelect) return undefined;
    const observer = new MutationObserver(() => revealOpenTypePanel(typeSelect));
    observer.observe(typeSelect, { subtree: true, attributes: true, attributeFilter: ['aria-expanded'] });
    return () => observer.disconnect();
  }, [selectedTier]);

  return (
    <div className="d-flex align-items-center justify-content-center">
      <div className="card listing-card mx-12 p-2 mb-2 flex-fill">
        <div className="dropdown-container">
          <div className="input-container">
            <div className="input-group form-floating-label fixed-label">
              <input
                name="keyName"
                autoFocus={!item?._id}
                value={item?.keyName ?? ''}
                placeholder={t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.ENTER_LABEL')}
                type="text"
                onChange={(event) => onChange(event.target.value, index, 'keyName')}
                className="form-control"
                disabled={!addEditAccess}
              />
              <label htmlFor="keyName" className="text-bold f-16 asterisk-req">
                {t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.LABEL')}
                <span>*</span>
              </label>
            </div>
            {item?.isEmpty && isEmpty(item?.keyName?.trim()) && (
              <label className="text-danger m-0 p-1">{t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.LABEL_REQUIRED')}</label>
            )}
          </div>

          <div className="input-action d-flex">
            {item?._id !== '' && addEditAccess && (
              <CustomTooltip tooltipText={activeStateTooltip}>
                <button type="button" className="mr-0" onClick={() => onToggleActive(item)} aria-label={activeStateLabel}>
                  <span className={item?.isActive ? 'icon-active f-24' : 'icon-inactive f-24'} />
                </button>
              </CustomTooltip>
            )}

            {item?.type === 'text' && addEditAccess && (
              <CustomTooltip tooltipText={t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.CHARACTER_COUNT')}>
                <button type="button" className="btn btn-outline mr-0" onClick={() => setShowCharLimit(true)} aria-label="Character limit">
                  <span className="icon-Edit f-24" />
                </button>
              </CustomTooltip>
            )}

            {hasOptions(item?.type) && (
              <>
                {addEditAccess && (
                  <CustomTooltip tooltipText={t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.ADD_OPTION')}>
                    <button
                      type="button"
                      className="mr-0"
                      onClick={() => {
                        onAddOption(index);
                        setOptionsOpen(true);
                      }}
                      aria-label="Add option"
                    >
                      <span className="icon-Add f-24" />
                    </button>
                  </CustomTooltip>
                )}
                <CustomTooltip tooltipText={t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.OPTIONS')}>
                  <button type="button" className="position-relative mr-0" onClick={() => setOptionsOpen((open) => !open)} aria-label="Toggle options">
                    <span
                      className={showOptions ? 'icon-new-down-arrow-icon f-24 icon-dropdown icon-inverse' : 'icon-new-down-arrow-icon f-24 icon-dropdown'}
                    />
                  </button>
                </CustomTooltip>
              </>
            )}

            {deleteAccess && (
              <CustomTooltip tooltipText={t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.DELETE')}>
                <button type="button" className="mr-0" onClick={() => onDelete(item, index)} aria-label="Delete">
                  <span className="icon-Delete f-24" />
                </button>
              </CustomTooltip>
            )}
          </div>
        </div>

        {duplicateIndexes?.includes(index) && <label className="text-danger m-0 p-1">{t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.ENTRY_EXIST')}</label>}

        <div className="common-react-select mb-1">
          <label htmlFor={`poca-tier-${index}`} className="select-label asterisk-req mb-0 text-bold">
            {t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.TIER')}
            <span>*</span>
          </label>
          <Select
            inputId={`poca-tier-${index}`}
            options={PARTNER_OVERVIEW_TIER_OPTIONS}
            value={selectedTierOption}
            getOptionLabel={(option: PartnerTierOption) => option.name}
            getOptionValue={(option: PartnerTierOption) => option._id}
            styles={{
              ...SystemMasterSelect,
              menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }),
            }}
            onChange={handleTierChange}
            isDisabled={!addEditAccess}
            placeholder={t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.TIER_PLACEHOLDER')}
            menuPlacement="auto"
            menuPosition="fixed"
            menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
            classNamePrefix={TIER_SELECT_CLASS_PREFIX}
            closeMenuOnScroll={isScrollOutsideTierMenu}
          />
          {item?.isEmpty && !selectedTier && <label className="text-danger m-0 p-1">{t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.TIER_REQUIRED')}</label>}
        </div>

        {selectedTier && (
          <div className="common-react-select mb-1" ref={typeSelectRef}>
            <CommonMultiSelect
              label={typeLabel}
              required
              onChange={(selected) =>
                onChange(
                  (selected ?? []).map((entry) => String(typeof entry === 'string' ? entry : entry?._id ?? entry)),
                  index,
                  'supplierType',
                )
              }
              selectedValue={toSelected(selectedTypeIds, tierTypes)}
              optionList={typeOptions}
              selectLabel="name"
              selectValue="_id"
              className="product-custom-multiselect common-new-multiselect w-100 text-bold mt-2"
              disable={!addEditAccess}
            />
            {item?.isEmpty && (item?.supplierType ?? []).length === 0 && (
              <label className="text-danger m-0 p-1">{t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.TYPE_REQUIRED', { type: typeLabel })}</label>
            )}
          </div>
        )}

        {hasOptions(item?.type) && (
          <div className={showOptions ? 'dropdown-items show' : 'dropdown-items'}>
            {!isEmpty(item?.options) ? (
              (item?.options ?? []).map((option, optionIndex) => (
                <div className="freetext-container mt-2 mx-0" key={`option_${index}_${optionIndex}`}>
                  <div className="input-container">
                    <div className="input-group form-floating-label fixed-label">
                      <input
                        name="optionKeyName"
                        type="text"
                        className="form-control"
                        autoFocus={!option?._id}
                        value={option?.keyName ?? ''}
                        placeholder={t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.ENTER_OPTION')}
                        onChange={(event) => onChangeOption(event.target.value, index, optionIndex, 'keyName')}
                        disabled={!addEditAccess}
                      />
                      <label htmlFor="optionKeyName" className="asterisk-req">
                        {t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.OPTION')} {optionIndex + 1}
                        <span>*</span>
                      </label>
                    </div>
                    {option?.isEmpty && isEmpty((option?.keyName ?? '').trim()) && (
                      <label className="text-danger m-0 p-1">{t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.OPTION_REQUIRED')}</label>
                    )}
                    {option?.isDuplicate && <label className="text-danger m-0 p-1">{t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.OPTION_EXIST')}</label>}
                  </div>
                  <div className="input-action d-flex">
                    {deleteAccess && (
                      <CustomTooltip tooltipText={t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.DELETE')}>
                        <button className=" mr-0" type="button" aria-label="Delete option" onClick={() => onDeleteOption(index, optionIndex)}>
                          <span className="icon-Delete f-24" />
                        </button>
                      </CustomTooltip>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="mt-3 mb-0 text-center">{t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.NO_OPTIONS')}</p>
            )}
          </div>
        )}
      </div>

      <Modal
        show={showCharLimit}
        backdrop="static"
        className="common-custom-modal modal-medium"
        aria-labelledby="contained-modal-title-vcenter"
        onHide={() => setShowCharLimit(false)}
      >
        <Modal.Header closeButton placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <Modal.Title>{t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.CHARACTER_COUNT')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FormGroup className="d-flex align-items-center mb-2">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={2}
              className="form-control text-bordered w-80px mr-2"
              value={item?.textFieldConfig?.charLimit ?? ''}
              disabled={!addEditAccess}
              onKeyDown={(event) => {
                const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
                if (allowed.includes(event.key)) return;
                if (!/^\d$/.test(event.key)) event.preventDefault();
              }}
              onPaste={(event) => {
                const pasted = event.clipboardData.getData('text');
                if (!/^\d+$/.test(pasted)) event.preventDefault();
              }}
              onChange={(event) => {
                const sanitized = event.target.value.replace(/\D/g, '');
                onChange(sanitized, index, 'textFieldConfig.charLimit');
              }}
            />
            <span>
              {t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.CHARACTER_LIMIT')} <small>({t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.MAX_CHAR')})</small>
            </span>
          </FormGroup>
          <FormGroup className="mb-2">
            <div className="custom-control custom-switch">
              <input
                type="checkbox"
                className="custom-control-input"
                id={`pocaEditableCheck${index}`}
                onChange={(event) => onChange(event.target.checked, index, 'textFieldConfig.isEditable')}
                checked={Boolean(item?.textFieldConfig?.isEditable)}
                disabled={!addEditAccess}
              />
              <label htmlFor={`pocaEditableCheck${index}`} className="custom-control-label">
                <span>{t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.EDITABLE_FIELD')}</span>
              </label>
            </div>
          </FormGroup>
          <div className="common-model-footer">
            <button type="button" aria-label="approve" className="btn p-0" onClick={() => setShowCharLimit(false)}>
              <span className="icon-Tick-1 f-24" />
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default AttributeCard;

const TIER_SELECT_CLASS_PREFIX = 'poca-tier';

/**
 * @description Closes the portalled Tier menu when anything but its own option list scrolls.
 * The menu is fixed to the viewport, so without this it stays put while the attribute list
 * scrolls underneath it and drifts away from its card.
 * @param event - the scroll event react-select observes.
 * @returns true when the menu should close.
 */
function isScrollOutsideTierMenu(event: Event): boolean {
  const { target } = event;
  return !(target instanceof Element && target.closest(`.${TIER_SELECT_CLASS_PREFIX}__menu-list`));
}

/**
 * @description Scrolls an open Type option panel into view. The attribute list is a fixed-height
 * scroll box, so a panel opened on a lower card renders below the box's visible edge. The
 * multiselect exposes no open callback, so its aria-expanded flip is observed instead.
 * @param typeSelect - the wrapper holding the Type multiselect.
 * @returns nothing; scrolls the nearest scroll containers as a side effect.
 */
function revealOpenTypePanel(typeSelect: HTMLElement): void {
  typeSelect.querySelector<HTMLElement>('.dropdown-content')?.scrollIntoView({ block: 'nearest' });
}
